import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch } from 'app/hooks';
import { setNameInConflict, setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { Claim } from 'common/types/Entities';
import { Operations } from 'common/types/TypeDefs';
import { handleClaimCreateFailure, handleClaimEditFailure } from 'common/utils/claimErrorHandlers';
import { useSnackbar } from 'notistack';

interface UseClaimMembershipDrawerOptions {
    /**
     * The drawer ID to match against (e.g., DrawerIDs.Claims, DrawerIDs.UserProfileClaims)
     */
    drawerId: string;
    /**
     * Current drawer ID from context
     */
    currentId: string | undefined;
    /**
     * Whether the drawer is currently open
     */
    isOpenDrawer: boolean;
    /**
     * Current drawer action
     */
    action: Operations.Create | Operations.Edit | Operations.Link | undefined;
    /**
     * Initial claim value for edit mode
     */
    editingClaimValue?: string;
    /**
     * Entity type for error messages
     */
    entityType?: 'User' | 'Role' | 'Claim';
}

interface UseClaimMembershipDrawerReturn {
    claim: Claim;
    setClaim: React.Dispatch<React.SetStateAction<Claim>>;
    claimValue: string;
    setClaimValue: React.Dispatch<React.SetStateAction<string>>;
    valueInConflict: boolean;
    setValueInConflict: React.Dispatch<React.SetStateAction<boolean>>;
    handleClaimChange: (_e: unknown, newClaim: Claim) => void;
    handleClaimValueChange: (value: string) => void;
    createFailureHandler: (error: unknown) => void;
    editFailureHandler: (error: unknown) => void;
}

const DEFAULT_CLAIM: Claim = {
    id: '__placeholder__',
    name: '',
    tenantId: '',
    claimDataTypeId: '',
    claimDataTypeName: ''
};

/**
 * Shared hook for claim membership drawer state management.
 * Consolidates common logic for UserClaimMembership, UserProfileClaims, and RoleClaimMembership drawers.
 */
export function useClaimMembershipDrawer({
    drawerId,
    currentId,
    isOpenDrawer,
    action,
    editingClaimValue,
    entityType = 'Claim'
}: UseClaimMembershipDrawerOptions): UseClaimMembershipDrawerReturn {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [claim, setClaim] = useState<Claim>(DEFAULT_CLAIM);
    const [claimValue, setClaimValue] = useState<string>('');
    const [valueInConflict, setValueInConflict] = useState<boolean>(false);

    // Reset state when drawer opens/closes or action changes
    useEffect(() => {
        if (currentId === drawerId) {
            if (action === Operations.Link) {
                setClaim(DEFAULT_CLAIM);
                setClaimValue('');
                return;
            }
            setClaimValue(isOpenDrawer ? editingClaimValue ?? '' : '');
            setValueInConflict(false);
            dispatch(setSaveButtonEnabled(false));
        }
    }, [isOpenDrawer, currentId, action, editingClaimValue, dispatch, drawerId]);

    const handleClaimChange = useCallback((_e: unknown, newClaim: Claim) => {
        if (newClaim.claimDataTypeName === 'Boolean') {
            setClaimValue('false');
        } else {
            setClaimValue('');
        }
        setClaim(newClaim);
        dispatch(setSaveButtonEnabled(
            newClaim.claimDataTypeName === 'Permission' ||
            newClaim.claimDataTypeName === 'Boolean'
        ));
        dispatch(setNameInConflict(false));
        setValueInConflict(false);
    }, [dispatch]);

    const handleClaimValueChange = useCallback((value: string) => {
        setClaimValue(value);
        dispatch(setSaveButtonEnabled(!!value));
        setValueInConflict(false);
    }, [dispatch]);

    const createFailureHandler = useCallback((error: unknown) => {
        handleClaimCreateFailure(error, { dispatch, enqueueSnackbar, entityType });
    }, [dispatch, enqueueSnackbar, entityType]);

    const editFailureHandler = useCallback((error: unknown) => {
        handleClaimEditFailure(error, { dispatch, enqueueSnackbar, setValueInConflict });
    }, [dispatch, enqueueSnackbar]);

    return {
        claim,
        setClaim,
        claimValue,
        setClaimValue,
        valueInConflict,
        setValueInConflict,
        handleClaimChange,
        handleClaimValueChange,
        createFailureHandler,
        editFailureHandler
    };
}
